"use client";

import { Check, Copy } from "lucide-react";
import dynamic from "next/dynamic";
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { copyTextWithToast } from "@/lib/client-clipboard";
import { cn } from "@/lib/utils";

type CodeProps = ComponentPropsWithoutRef<"code"> & {
  renderedCode?: ReactNode;
  node?: { position?: { start: { line: number }; end: { line: number } } };
};

const ChartBlock = dynamic(
  () =>
    import("@/components/content/chart-block").then((mod) => mod.ChartBlock),
  { ssr: false },
);

function extractText(children: ReactNode): string {
  if (typeof children === "string") return children;
  if (Array.isArray(children)) {
    return children.map(extractText).join("");
  }
  return "";
}

function getLanguage(className: string | undefined) {
  const match = className?.match(/language-([\w-]+)/);
  return match?.[1] ?? "text";
}

export function CodeBlock({ children, className, node, renderedCode, ...rest }: CodeProps) {
  const isBlock =
    className?.startsWith("language-") ||
    (node?.position
      ? node.position.start.line !== node.position.end.line
      : false);

  if (isBlock) {
    // Intercept the `chart` language so fenced blocks render as recharts.
    // Any non-chart language falls through to the standard fenced renderer.
    if (getLanguage(className) === "chart") {
      return (
        <div className="not-typeset">
          <ChartBlock source={extractText(children)} />
        </div>
      );
    }
    return (
      <FencedCode className={className} renderedCode={renderedCode} {...rest}>
        {children}
      </FencedCode>
    );
  }

  return (
    <code
      className={cn(
        "box-decoration-clone rounded-[0.22em] bg-content-code/45 px-[0.3em] py-[0.05em] font-mono text-[0.92em] font-normal leading-[inherit] text-content-code-foreground",
        className,
      )}
      {...rest}
    >
      {children}
    </code>
  );
}

function FencedCode({ children, className, renderedCode, ...rest }: Omit<CodeProps, "node">) {
  const code = useMemo(() => extractText(children), [children]);
  const language = useMemo(() => getLanguage(className), [className]);
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const handleCopy = useCallback(async () => {
    const copiedText = await copyTextWithToast(code, {
      successTitle: "Code copied",
      errorTitle: "Failed to copy code",
    });
    if (copiedText) {
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 2000);
    }
  }, [code]);

  return (
    <div className="not-typeset group/code my-4 overflow-hidden rounded-lg border border-border/60 bg-muted/40">
      <div className="flex items-center justify-between gap-2 border-b border-border/60 bg-muted/30 px-3 py-1.5">
        <div className="truncate text-2xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
          {language}
        </div>
        <Button
          type="button"
          size="xs"
          variant="ghost"
          aria-label={copied ? "Copied code" : "Copy code"}
          className="opacity-0 transition-opacity group-hover/code:opacity-100 group-focus-within/code:opacity-100"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void handleCopy();
          }}
        >
          {copied ? (
            <Check data-icon="inline-start" />
          ) : (
            <Copy data-icon="inline-start" />
          )}
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
      <pre className="overflow-x-auto scrollbar-thin px-4 py-3 text-sm leading-relaxed">
        <code className={cn("font-mono", className)} {...rest}>
          {renderedCode ?? children}
        </code>
      </pre>
    </div>
  );
}
